// src/components/SellerDashboard.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext'; 
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// --- Chart.js setup ---
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

// Dummy Data
const DUMMY_STATS = {
    inquiries: 125,
    read: 100,
    unread: 25,
    leads: 15,
};

// --- StatCard Component ---
interface StatCardProps {
    title: string;
    value: number | string;
    iconColor: string;
    icon: React.ReactNode; 
    link: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, iconColor, icon, link }) => (
    <Link to={link} className="bg-white p-6 rounded-lg shadow-xl hover:shadow-2xl transition duration-300 transform hover:-translate-y-1 border-t-4 border-gray-100 flex flex-col justify-between">
        <div className="flex items-center justify-between mb-4">
            <p className="text-4xl font-extrabold text-gray-900">{value}</p>
            <div className={`p-3 rounded-full ${iconColor} bg-opacity-10`}>
                <span className={`w-8 h-8 ${iconColor.replace('bg-', 'text-')}`}>
                    {icon}
                </span>
            </div>
        </div>
        <div className="pt-2 border-t border-gray-100 flex justify-between items-center">
            <h3 className="text-sm font-medium text-gray-500 uppercase">{title}</h3>
            <span className="text-sm text-blue-600 hover:text-blue-800 font-medium transition whitespace-nowrap">
                Details →
            </span>
        </div>
    </Link>
);

// --- Icons ---
const IconEnvelope = <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>;
const IconMailOpen = <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>; 
const IconBell = <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path><path d="M13.73 21a2 2 0 0 1-3.46 0"></path></svg>;
const IconFire = <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"></path></svg>; 

// --- Product Status Item ---
const StatusItem: React.FC<{ color: string, label: string, value: number }> = ({ color, label, value }) => (
    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-md border-l-4" style={{ borderColor: color.replace('bg','') }}>
        <span className="flex items-center font-medium text-gray-700">
            <span className={`w-3 h-3 ${color} rounded-full mr-3`}></span>
            {label}
        </span>
        <span className="font-bold text-2xl text-gray-800">{value}</span>
    </div>
);

// --- Performance Chart Component ---
const PerformanceChart: React.FC = () => {
    const data = {
        labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
        datasets: [
            {
                label: "Inquiries",
                data: [65, 59, 80, 81, 56, 55, 70],
                fill: true,
                backgroundColor: "rgba(34,197,94,0.1)",
                borderColor: "rgb(34,197,94)",
                tension: 0.4,
                pointBackgroundColor: "rgb(34,197,94)",
                pointBorderColor: "#fff",
                pointHoverRadius: 6,
            },
        ],
    };

   const options: ChartOptions<'line'> = {
  responsive: true,
  animation: { duration: 1500, easing: 'easeOutQuart' },
  plugins: {
    legend: { display: false },
    tooltip: { mode: 'index', intersect: false },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: '#4B5563', font: { size: 12 } },
    },
    y: {
      grid: { color: '#E5E7EB' },
      ticks: { color: '#4B5563', font: { size: 12 } },
    },
  },
};
    return (
       <div className="h-72">
    <Line data={data} options={options} />
</div>
    );
};

// --- SellerDashboard Main ---
const SellerDashboard: React.FC = () => {
    const { user } = useUser();
    const sellerName = user?.username || "Nadezhda"; 

    return (
        <div className="space-y-8">

            {/* Welcome */}
            <div className="pb-4 border-b border-gray-200">
                <h1 className="text-3xl font-bold text-gray-900">Welcome Back, {sellerName}!</h1>
                <p className="text-gray-500 mt-1">Here is a summary of your B2Bhaart Seller activity.</p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard title="Total Inquiries Received" value={DUMMY_STATS.inquiries} iconColor="bg-blue-600" icon={IconEnvelope} link="/seller/inquiries"/>
                <StatCard title="Read Inquiries" value={DUMMY_STATS.read} iconColor="bg-green-600" icon={IconMailOpen} link="/seller/inquiries?status=read"/>
                <StatCard title="Unread Inquiries" value={DUMMY_STATS.unread} iconColor="bg-yellow-600" icon={IconBell} link="/seller/inquiries?status=unread"/>
                <StatCard title="Recent Buy Trade Leads" value={DUMMY_STATS.leads} iconColor="bg-red-600" icon={IconFire} link="/seller/leads"/>
            </div>

            {/* Analytics + Product Status */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Chart */}
                <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow-xl border border-gray-100">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">Inquiry Performance Analytics</h2>
                    <div className="flex justify-end items-center mb-4 space-x-2">
                        <select className="p-2 border rounded-md text-sm">
                            <option>Last 30 Days</option>
                            <option>Last 90 Days</option>
                        </select>
                    </div>
                    <PerformanceChart />
                </div>

                {/* Product Status */}
                <div className="lg:col-span-1 bg-white p-6 rounded-lg shadow-xl border border-gray-100">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl font-semibold text-gray-800">Products Status Summary</h2>
                        <Link to="/seller/products/add" className="text-sm font-bold text-red-600 flex items-center hover:underline">+ Add New</Link>
                    </div>
                    <div className="mt-4 space-y-4">
                        <StatusItem color="bg-green-600" label="Live Products" value={60} />
                        <StatusItem color="bg-yellow-600" label="Pending for Review" value={3} />
                        <StatusItem color="bg-red-600" label="Drafts" value={2} />
                        <StatusItem color="bg-gray-400" label="Out of Stock" value={0} />
                    </div>
                    <div className="mt-8 text-center border-t pt-4">
                         <Link to="/seller/products" className="text-sm text-blue-600 hover:text-blue-800 font-medium transition">
                            Manage All Products →
                        </Link>
                    </div>
                </div>
            </div>

            {/* Recent Activity */}
             <div className="bg-white p-6 rounded-lg shadow-xl border border-gray-100">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Recent Activity (Latest Orders/Inquiries)</h2>
                <div className="text-gray-500 py-8 text-center border-dashed border-2 rounded-md">
                    No recent activity found. 
                </div>
            </div>
        </div>
    );
};

export default SellerDashboard;
